// File: src/config/navigation.ts
// Purpose: Complete navigation structure for InstaDev Healthcare Platform
// InstaDev - Navigation Configuration

import { MenuItem } from '@/types/navigation'
import {
  Sparkles,
  CheckSquare,
  Bell,
  MessageSquare
} from 'lucide-react'

export const navigation: MenuItem[] = [
  // Dashboard
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    href: '/dashboard',
    children: [
      {
        id: 'dashboard-overview',
        label: 'Overview',
        href: '/dashboard'
      },
      {
        id: 'dashboard-activity',
        label: 'Activity Feed',
        href: '/dashboard/activity'
      },
      {
        id: 'dashboard-stats',
        label: 'Statistics',
        href: '/dashboard/stats'
      }
    ]
  },

  // AI Generation
  {
    id: 'generation',
    label: 'AI Generation',
    icon: 'Sparkles',
    children: [
      {
        id: 'generation-hub',
        label: 'Generation Hub',
        href: '/generation',
        badge: 'Hub'
      },
      {
        id: 'new-generation',
        label: 'New Generation',
        href: '/generation/new'
      },
      {
        id: 'code-generation',
        label: 'Code Generation',
        href: '/generation/code'
      },
      {
        id: 'test-generation',
        label: 'Test Generation',
        href: '/generation/tests'
      },
      {
        id: 'docs-generation',
        label: 'Documentation',
        href: '/generation/docs'
      },
      {
        id: 'ai-chat',
        label: 'AI Chat Assistant',
        href: '/ai-chat',
        badge: 'New'
      },
      {
        id: 'ai-personas',
        label: 'AI Personas',
        href: '/ai-personas'
      },
      {
        id: 'prompt-library',
        label: 'Prompt Library',
        href: '/prompt-library'
      },
      {
        id: 'context-management',
        label: 'Context Management',
        href: '/context-management'
      }
    ]
  },

  // Projects
  {
    id: 'projects',
    label: 'Projects',
    icon: 'FolderKanban',
    children: [
      {
        id: 'all-projects',
        label: 'All Projects',
        href: '/projects'
      },
      {
        id: 'new-project',
        label: 'Create Project',
        href: '/projects/new'
      },
      {
        id: 'project-status',
        label: 'Project Status',
        href: '/projects/status'
      },
      {
        id: 'project-team',
        label: 'Team Management',
        href: '/project-team-management'
      }
    ]
  },

  // Development
  {
    id: 'development',
    label: 'Development',
    icon: 'Code',
    children: [
      {
        id: 'code-editor',
        label: 'Code Editor',
        href: '/development/code-editor'
      },
      {
        id: 'scaffolding',
        label: 'Scaffolding',
        href: '/development/scaffolding'
      },
      {
        id: 'version-control',
        label: 'Version Control',
        href: '/development/version-control'
      },
      {
        id: 'commits',
        label: 'Commits',
        href: '/development/commits'
      },
      {
        id: 'testing',
        label: 'Testing',
        href: '/development/testing'
      },
      {
        id: 'diagnostics',
        label: 'Diagnostics',
        href: '/development/diagnostics'
      },
      {
        id: 'api-builder',
        label: 'API Builder',
        href: '/development/api-builder'
      },
      {
        id: 'review-queue',
        label: 'Review Queue',
        href: '/review-queue',
        badge: '3'
      }
    ]
  },

  // Tasks & Assignments
  {
    id: 'tasks',
    label: 'Tasks',
    icon: 'CheckSquare',
    children: [
      {
        id: 'all-tasks',
        label: 'All Tasks',
        href: '/tasks'
      },
      {
        id: 'assignments',
        label: 'Assignments',
        href: '/assignments'
      }
    ]
  },

  // Employee Portal
  {
    id: 'employee',
    label: 'Employee',
    icon: 'UserCheck',
    children: [
      {
        id: 'employee-dashboard',
        label: 'Dashboard',
        href: '/employee-dashboard'
      },
      {
        id: 'employee-projects',
        label: 'My Projects',
        href: '/employee-projects'
      },
      {
        id: 'employee-tasks',
        label: 'My Tasks',
        href: '/employee-tasks'
      },
      {
        id: 'employee-timesheet',
        label: 'Timesheet',
        href: '/employee-timesheet'
      },
      {
        id: 'employee-reports',
        label: 'Reports',
        href: '/employee-reports'
      }
    ]
  },

  // Organization
  {
    id: 'organization',
    label: 'Organization',
    icon: 'Building',
    children: [
      {
        id: 'departments',
        label: 'Departments',
        href: '/departments'
      },
      {
        id: 'teams',
        label: 'Teams',
        href: '/team-hub'
      },
      {
        id: 'employees',
        label: 'Employees',
        href: '/employees',
        children: [
          {
            id: 'employees-list',
            label: 'Employee List',
            href: '/employees'
          },
          {
            id: 'employee-skills',
            label: 'Skills Management',
            href: '/admin/employee-skills',
            badge: 'New'
          }
        ]
      },
      {
        id: 'roles',
        label: 'Roles',
        href: '/organization/roles'
      },
      {
        id: 'resources',
        label: 'Resources',
        href: '/resource-management'
      }
    ]
  },

  // Analytics
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'BarChart3',
    children: [
      {
        id: 'analytics-overview',
        label: 'Overview',
        href: '/analytics'
      },
      {
        id: 'personal',
        label: 'Personal',
        href: '/analytics/personal'
      },
      {
        id: 'team',
        label: 'Team',
        href: '/analytics/team'
      },
      {
        id: 'project',
        label: 'Projects',
        href: '/analytics/projects'
      },
      {
        id: 'department',
        label: 'Department',
        href: '/analytics/department'
      },
      {
        id: 'platform',
        label: 'Platform',
        href: '/analytics/platform'
      },
      {
        id: 'performance',
        label: 'Performance',
        href: '/analytics/performance'
      },
      {
        id: 'usage',
        label: 'Usage',
        href: '/analytics/usage'
      },
      {
        id: 'insights',
        label: 'Insights',
        href: '/analytics/insights'
      },
      {
        id: 'reports',
        label: 'Reports',
        href: '/analytics/reports'
      }
    ]
  },

  // Billing
  {
    id: 'billing',
    label: 'Billing',
    icon: 'CreditCard',
    children: [
      {
        id: 'billing-overview',
        label: 'Overview',
        href: '/billing'
      },
      {
        id: 'usage',
        label: 'Usage',
        href: '/billing/usage'
      },
      {
        id: 'budgets',
        label: 'Budgets',
        href: '/billing/budgets'
      },
      {
        id: 'allocations',
        label: 'Allocations',
        href: '/billing/allocations'
      },
      {
        id: 'models',
        label: 'AI Models',
        href: '/billing/models'
      },
      {
        id: 'costs',
        label: 'Costs',
        href: '/billing/costs'
      },
      {
        id: 'billing-analytics',
        label: 'Analytics',
        href: '/billing/analytics'
      },
      {
        id: 'export',
        label: 'Export',
        href: '/billing/export'
      },
      {
        id: 'subscription',
        label: 'Subscription',
        href: '/billing/subscription'
      }
    ]
  },

  // Integrations
  {
    id: 'integrations',
    label: 'Integrations',
    icon: 'Plug',
    children: [
      {
        id: 'integration-center',
        label: 'Integration Center',
        href: '/integration-center'
      },
      {
        id: 'api-management',
        label: 'API Management',
        href: '/integration-center/api-management'
      },
      {
        id: 'browser-automation',
        label: 'Browser Automation',
        href: '/integration-center/browser-automation'
      },
      {
        id: 'healthcare',
        label: 'Healthcare',
        href: '/integration-center/healthcare'
      },
      {
        id: 'third-party',
        label: 'Third Party',
        href: '/integration-center/third-party'
      },
      {
        id: 'api-gateway',
        label: 'API Gateway',
        href: '/integrations/api-gateway'
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        href: '/integrations/webhooks'
      },
      {
        id: 'ehr',
        label: 'EHR Systems',
        href: '/integrations/ehr'
      },
      {
        id: 'automation',
        label: 'Automation',
        href: '/integrations/automation'
      }
    ]
  },

  // Deployment
  {
    id: 'deployment',
    label: 'Deployment',
    icon: 'Rocket',
    children: [
      {
        id: 'deployment-center',
        label: 'Deployment Center',
        href: '/deployment-center'
      },
      {
        id: 'deployment-overview',
        label: 'Overview',
        href: '/deployment'
      }
    ]
  },

  // Compliance
  {
    id: 'compliance',
    label: 'Compliance',
    icon: 'Shield',
    children: [
      {
        id: 'compliance-center',
        label: 'Compliance Center',
        href: '/compliance-center'
      },
      {
        id: 'compliance-overview',
        label: 'Overview',
        href: '/compliance'
      },
      {
        id: 'healthcare-protocols',
        label: 'Healthcare Protocols',
        href: '/healthcare-protocols'
      }
    ]
  },

  // Resources
  {
    id: 'resources',
    label: 'Resources',
    icon: 'BookOpen',
    children: [
      {
        id: 'resource-center',
        label: 'Resource Center',
        href: '/resource-center'
      },
      {
        id: 'templates',
        label: 'Templates',
        href: '/resources/templates'
      },
      {
        id: 'documentation',
        label: 'Documentation',
        href: '/resources/documentation'
      }
    ]
  },

  // Admin
  {
    id: 'admin',
    label: 'Admin',
    icon: 'ShieldCheck',
    children: [
      {
        id: 'users',
        label: 'Users',
        href: '/admin/users'
      },
      {
        id: 'roles',
        label: 'Roles',
        href: '/admin/roles'
      },
      {
        id: 'config',
        label: 'Configuration',
        href: '/admin/config'
      },
      {
        id: 'licenses',
        label: 'Licenses',
        href: '/admin/licenses'
      },
      {
        id: 'monitoring',
        label: 'Monitoring',
        href: '/admin/monitoring'
      },
      {
        id: 'infrastructure',
        label: 'Infrastructure',
        href: '/admin/infrastructure'
      },
      {
        id: 'health',
        label: 'System Health',
        href: '/admin/health'
      }
    ]
  },

  // Settings
  {
    id: 'settings',
    label: 'Settings',
    icon: 'Settings',
    children: [
      {
        id: 'profile',
        label: 'Profile',
        href: '/dashboard/settings/profile'
      },
      {
        id: 'preferences',
        label: 'Preferences',
        href: '/settings/preferences'
      },
      {
        id: 'notifications-settings',
        label: 'Notifications',
        href: '/settings/notifications'
      },
      {
        id: 'project-settings',
        label: 'Project Settings',
        href: '/settings/project'
      },
      {
        id: 'team-settings',
        label: 'Team Settings',
        href: '/settings/team'
      },
      {
        id: 'api-keys',
        label: 'API Keys',
        href: '/settings/api-keys'
      }
    ]
  },

  // Notifications
  {
    id: 'notifications',
    label: 'Notifications',
    icon: 'Bell',
    href: '/notifications',
    badge: '5'
  }
]

// Quick access items for header
export const quickAccessItems = [
  { id: 'quick-generate', title: 'Quick Generate', icon: Sparkles, href: '/generation/new' },
  { id: 'active-tasks', title: 'Active Tasks', icon: CheckSquare, href: '/tasks' },
  { id: 'notifications', title: 'Notifications', icon: Bell, href: '/notifications' }
]

// Helper function to filter navigation based on user role (simplified for now)
export function getNavigationForRole(userRole: string): MenuItem[] {
  // For now, return all navigation items
  // In a real implementation, you would filter based on roles
  return navigation
}